import { FC, useMemo } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { CompositeFillableGapsData } from "@app/overviews/enterprise-compliance-dashboard/interfaces";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

const barChartProps = {
  barCategoryGap: 0,
  barGap: 0,
  margin: {
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
  preserveAspectRatio: "none",
}

interface CompositeFillableGapsChartProps {
  compositeFillableGapsData: CompositeFillableGapsData;
  fillableGapsPrimary: string;
  fillableGapsSecondary: string;
  unfillableGaps: string;
}

const CompositeFillableGapsChart: FC<CompositeFillableGapsChartProps> = (props: CompositeFillableGapsChartProps) => {

  const barProps = useMemo(() => {
    return {
      background: { fill: props.unfillableGaps },
      dataKey: "gapPercentage",
      minPointSize: 1,
    }
  }, [props.unfillableGaps]);

  // These are arrays purely because that's what recharts will accept
  // fillablePercentage and fillableByOnePercentage should never be above 100, so don't need localeString formatting
  const beforeData = useMemo(() => {
    return [
      {
        gapNumber: props.compositeFillableGapsData?.fillableGapCount.toLocaleString(),
        gapPercentage: props.compositeFillableGapsData?.fillablePercentage,
        gapPercentageString: props.compositeFillableGapsData?.fillablePercentage !== undefined ?
          `${props.compositeFillableGapsData?.fillablePercentage}%` :
          "---",
      }
    ]
  }, [props.compositeFillableGapsData]);

  const afterData = useMemo(() => {
    return [
      {
        gapNumber: props.compositeFillableGapsData?.fillableByOneGapCount.toLocaleString(),
        gapPercentage: props.compositeFillableGapsData?.fillableByOnePercentage,
        gapPercentageString: props.compositeFillableGapsData?.fillableByOnePercentage !== undefined ?
          `${props.compositeFillableGapsData?.fillableByOnePercentage}%` :
          "---",
      }
    ]
  }, [props.compositeFillableGapsData]);


  return (
    <div className="d-flex justify-content-end flex-column" style={{ margin: "0 auto", width: "90%" }}>
      <div className="d-flex justify-content-between align-items-end">
        <span style={{ flexBasis: "40%" }}>{beforeData[0].gapPercentageString} of {props.compositeFillableGapsData?.gapCount.toLocaleString()} gaps are fillable</span>
        <QesIcon iconName={QesIcons.ArrowNarrowRight} isDecorative />
        <span style={{ flexBasis: "40%", textAlign: "right" }}>{afterData[0].gapPercentageString} of fillable gaps are fillable with 1 provider</span>
      </div>
      <div className="d-flex align-items-end justify-content-between" style={{ height: "230px" }}>
        <ResponsiveContainer width="30%" height="100%">
          <BarChart
            width={120}
            height={300}
            data={beforeData}
            {...barChartProps}
          >
            <CartesianGrid strokeDasharray="0 3" />
            <XAxis
              height={0}
              hide={true}
            />
            <YAxis hide={true} domain={[1, 100]} />
            <Bar {...barProps} fill={props.fillableGapsPrimary}>
              <LabelList
                dataKey="gapNumber"
                position={props.compositeFillableGapsData?.fillableByOnePercentage < 10 ? "top" : "insideTop"}
                fill={props.compositeFillableGapsData?.fillableByOnePercentage < 10 ? "#000000" : "#ffffff"}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <ResponsiveContainer width="30%" height="100%">
          <BarChart
            width={120}
            height={300}
            data={afterData}
            {...barChartProps}
          >
            <CartesianGrid strokeDasharray="0 3" />
            <XAxis
              height={30}
              hide={true}
            />
            <YAxis hide={true} domain={[1, 100]} />
            <Bar {...barProps} fill={props.fillableGapsSecondary}>
              <LabelList
                dataKey="gapNumber"
                position={props.compositeFillableGapsData?.fillablePercentage < 10 ? "top" : "insideTop"}
                fill="#000000"
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default CompositeFillableGapsChart;